import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LoadingToastComponent } from 'src/app/layouts/toast/loading-toast.component';
import { Utilities } from '../utilities';

@Injectable()
export class AlertService {
    private static readonly defaultSavingOKMessage: string = 'Operazione terminata con successo';

    private dialogs = new Subject<AlertDialog>();
    private toastRef;
    private _isLoading = false;

    constructor(private toastr: ToastrService) {}

    public showDialog(message: string);
    public showDialog(message: string, type: DialogType, okCallback: (val?: any) => any);
    public showDialog(message: string, type: DialogType, okCallback?: (val?: any) => any, cancelCallback?: () => any, okLabel?: string, cancelLabel?: string, defaultValue?: string);
    public showDialog(message: string, type?: DialogType, okCallback?: (val?: any) => any, cancelCallback?: () => any, okLabel?: string, cancelLabel?: string, defaultValue?: string) {
        if (!type) {
            type = DialogType.alert;
        }
        this.dialogs.next({ message: message, type: type, okCallback: okCallback, cancelCallback: cancelCallback, okLabel: okLabel, cancelLabel: cancelLabel, defaultValue: defaultValue });
    }

    public showMessage(summary: string);
    public showMessage(summary: string, detail: string, severity: MessageSeverity);
    public showMessage(summary: string, detail: string, severity: MessageSeverity, timeout: number);
    public showMessage(summaryAndDetails: string[], summaryAndDetailsSeparator: string, severity: MessageSeverity);
    public showMessage(response: Response, ignoreValue_useNull: string, severity: MessageSeverity);
    public showMessage(data: any, separatorOrDetail?: string, severity?: MessageSeverity, timeout: number = 4000000) {
        if (!severity) {
            severity = MessageSeverity.default;
        }
        if (data instanceof Response) {
            data = Utilities.getHttpResponseMessage(data);
            separatorOrDetail = Utilities.captionAndMessageSeparator;
        }
        if (data instanceof Array) {
            for (let message of data) {
                let msgObject = Utilities.splitInTwo(message, separatorOrDetail);
                this.showMessageHelper(msgObject.firstPart, msgObject.secondPart, severity, false);
            }
        } else {
            this.showMessageHelper(data, separatorOrDetail, severity, false);
        }
    }

    public showStickyMessage(summary: string);
    public showStickyMessage(summary: string, detail: string, severity: MessageSeverity, error?: any);
    public showStickyMessage(summaryAndDetails: string[], summaryAndDetailsSeparator: string, severity: MessageSeverity);
    public showStickyMessage(response: Response, ignoreValue_useNull: string, severity: MessageSeverity);
    public showStickyMessage(data: string | string[] | Response, separatorOrDetail?: string, severity?: MessageSeverity, error?: any) {
        if (!severity) {
            severity = MessageSeverity.default;
        }
        if (data instanceof Response) {
            data = Utilities.getHttpResponseMessage(data);
            separatorOrDetail = Utilities.captionAndMessageSeparator;
        }
        if (data instanceof Array) {
            for (let message of data) {
                let msgObject = Utilities.splitInTwo(message, separatorOrDetail);
                this.showMessageHelper(msgObject.firstPart, msgObject.secondPart, severity, true);
            }
        } else {
            if (error) {
                let msg = `Gravità: "${MessageSeverity[severity]}", Riepilogo: "${data}", Dettagli: "${separatorOrDetail}", Errore: "${Utilities.safeStringify(error)}"`;
                switch (severity) {
                    case MessageSeverity.default:
                        console.info(msg);
                        break;
                    case MessageSeverity.info:
                        console.info(msg);
                        break;
                    case MessageSeverity.success:
                        console.log(msg);
                        break;
                    case MessageSeverity.error:
                        console.error(msg);
                        break;
                    case MessageSeverity.warn:
                        console.warn(msg);
                        break;
                    case MessageSeverity.wait:
                        console.trace(msg);
                        break;
                }
            }
            this.showMessageHelper(data, separatorOrDetail, severity, true);
        }
    }

    private showMessageHelper(detail: any, summary: string, severity: MessageSeverity, isSticky: boolean) {
        switch (severity) {
            case MessageSeverity.default:
                this.toastr.info(summary, detail);
                break;
            case MessageSeverity.info:
                this.toastr.info(summary, detail);
                break;
            case MessageSeverity.success:
                this.toastr.success(summary, detail);
                break;
            case MessageSeverity.error:
                this.toastr.error(summary, detail);
                break;
            case MessageSeverity.warn:
                this.toastr.warning(summary, detail);
                break;
            case MessageSeverity.wait:
                this.toastr.info(summary, detail, {
                    progressBar: true,
                    progressAnimation: 'increasing',
                });
                break;
        }
    }

    public startLoadingMessage(message?, title = '') {
        if (!message) {
            message = 'Caricamento in corso';
        }
        this.toastRef = this.toastr.show(message, title, {
            toastComponent: LoadingToastComponent,
            timeOut: 200000,
        });
    }

    public stopLoadingMessage() {
        this.toastr.clear(this.toastRef.toastId);
    }

    /**
     * Effettua l'esposizione della finestra del messaggio di salvataggio.
     * @param message messaggio (opzionale, il default è "Salvataggio")
     * @param caption titolo della finestra (opzionale il default è "")
     */
    public startSavingMessage(message = '', caption = 'Salvataggio...') {
        this.toastRef = this.toastr.show(message, caption, {
            toastComponent: LoadingToastComponent,
            timeOut: 2000000,
        });
    }

    /**
     * Standard saving message
     */
    public savingMessageSuccess(message: string = AlertService.defaultSavingOKMessage) {
        this.showMessage('Salvataggio', message, MessageSeverity.success);
    }

    /**
     * Standard info message
     */
    public messageInfo(message = 'message info', caption = 'Title info', timeout: number) {
        this.showMessage(caption, message, MessageSeverity.info, timeout);
    }

    /**
     * Standard warning message
     */
    public messageWarning(caption = 'Title info', message = 'message warning', timeout: number) {
        this.showMessage(caption, message, MessageSeverity.warn, timeout);
    }

    /**
     * Standard saving message on error
     */
    public savingMessageError(error: any) {
        this.showMessage('Errore salvataggio', 'Errore durante il salvataggio a sistema', MessageSeverity.error, error);
    }

    get isLoadingInProgress(): boolean {
        return this._isLoading;
    }
}

export class AlertDialog {
    constructor(public message: string, public type: DialogType, public okCallback: (val?: any) => any, public cancelCallback: () => any, public defaultValue: string, public okLabel: string, public cancelLabel: string) {}
}

export enum DialogType {
    alert,
    confirm,
    prompt,
}
export class AlertMessage {
    constructor(public severity: MessageSeverity, public summary: string, public detail: string, public timeout: number = 400000) {}
}

export enum MessageSeverity {
    default,
    info,
    success,
    error,
    warn,
    wait,
    warning,
}
